import { NgIf } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { HaftpflichtSchaden } from '@gsm/entities';
import { getDisplayName } from '../../helpers/display-name.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { GeschaedigtePersonDialogData, HaftpflichtGeschaedigtePersonDialogComponent } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person-dialog/haftpflicht-geschaedigte-person-dialog.component';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { HAFTPFLICHT_SACHSCHADEN_KABEL_LEITUNG_TEMPLATE } from './haftpflicht-sachschaden-kabel-leitung.config';
import { HaftpflichtSachschadenKabelLeitungModel } from './haftpflicht-sachschaden-kabel-leitung.model';

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-haftpflicht-sachschaden-kabel-leitung',
  templateUrl: 'haftpflicht-sachschaden-kabel-leitung.component.html',
  styleUrls:   ['haftpflicht-sachschaden-kabel-leitung.component.scss'],
  standalone:  true,
  imports:     [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    NgIf,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMiniFabButton
  ]
})
export class HaftpflichtSachschadenKabelLeitungComponent extends CustomDynamicFormControlComponent<HaftpflichtSachschadenKabelLeitungModel>
  implements OnInit {

  private dialog = inject(MatDialog);

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    if (!this.model.value) {
      this.model.value = [];
    }
    this._emitChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = this.model.value?.length;
    return isValid ? null : {sachschadenKabelLeitung: true};
  }

  getName(schaden: HaftpflichtSchaden): string {
    return schaden.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
      ? 'Wird nachgebracht'
      : schaden.geschaedigtePerson?.nachname
        ? getDisplayName([
          schaden.geschaedigtePerson.vorname,
          schaden.geschaedigtePerson.nachname
        ])
        : 'Unbekannt';
  }

  onAddClick() {
    this._editItem({}, null);
  }

  onEditClick(item: HaftpflichtSchaden, index: number) {
    this._editItem(item, index, true);
  }

  onRemoveClick(index: number) {
    this.model.value = this.model.value.filter((_, i) => i !== index);
    this._emitChange();
  }

  private _editItem(item: HaftpflichtSchaden, index: number | null, isEditEvent = false) {
    this.dialog
      .open<HaftpflichtGeschaedigtePersonDialogComponent, GeschaedigtePersonDialogData, AnyFormControlModel[]>(
        HaftpflichtGeschaedigtePersonDialogComponent,
        {
          // allow quick closing only in view mode
          disableClose: !this.model.disabled,
          minWidth:     '600px',
          data:         {
            formConfig:      HAFTPFLICHT_SACHSCHADEN_KABEL_LEITUNG_TEMPLATE(item, this.model.value, this.model.additional, isEditEvent),
            heading:         'Sachschaden Kabel/Leitung - Geschädigte Person',
            disabled:        this.model.disabled,
            parentComponent: this
          }
        }
      )
      .afterClosed()
      .subscribe(result => {
        if (result) {
          const resultEntity = this._getValueFromForm(result);
          this._saveEditedItem(resultEntity, index);
        }
      });
  }

  private _saveEditedItem(item: HaftpflichtSchaden, index: number | null) {
    // index == null means that a new item should be added
    if (index !== null) {
      this.model.value = this.model.value.map((val, i) => (index === i ? item : val));
    } else {
      this.model.value = [
        ...this.model.value,
        item
      ];
    }
    this._emitChange();
  }

  private _getValueFromForm(formModel: AnyFormControlModel[]): HaftpflichtSchaden {
    const {
      geschaedigtePerson: {
                            id,
                            vorhanden,
                            geschaedigtePerson
                          } = {} as any
    } = getGroupValue(formModel);

    return filterEmptyProps<HaftpflichtSchaden>({
      id,
      vorhanden,
      geschaedigtePerson
    });
  }
}
